/**
 * Assumptions & terms.
 *
 * The approved assumptions are shown read-only and cannot be excluded or
 * edited — they are contractual language agreed with legal, and in the document
 * they are still the template's own XML. Sales adds engagement-specific
 * assumptions alongside them, which print after the approved ones and are
 * marked CUSTOM.
 */
import type { CustomAssumption } from '../../lib/api';
import { Button, Card, Input } from '../../components/ui';
import { StateBadge } from './ItemEditor';

export function AssumptionsEditor({
  approved,
  custom,
  onChange,
  disabled,
}: {
  approved: string[];
  custom: CustomAssumption[];
  onChange(next: CustomAssumption[]): void;
  disabled: boolean;
}) {
  const add = () =>
    onChange([...custom, { id: `assumption-${Date.now().toString(36)}`, text: '' }]);

  const patch = (id: string, text: string) =>
    onChange(custom.map((item) => (item.id === id ? { ...item, text } : item)));

  const remove = (id: string) => onChange(custom.filter((item) => item.id !== id));

  return (
    <Card
      title="Assumptions & terms"
      description="Approved assumptions are administrator-controlled. Add engagement-specific ones below."
    >
      <ul className="space-y-2">
        {approved.map((text) => (
          <li key={text} className="flex items-start gap-2.5 text-[13px]">
            <span aria-hidden="true" className="mt-0.5 shrink-0 text-muted">
              🔒
            </span>
            <span className="min-w-0 flex-1 text-body">{text}</span>
            <span className="shrink-0 rounded bg-tint px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted">
              Locked
            </span>
          </li>
        ))}
      </ul>

      {custom.length > 0 && (
        <ul className="mt-4 space-y-2 border-t border-rule pt-4">
          {custom.map((assumption) => (
            <li key={assumption.id} className="flex items-start gap-2.5">
              <span className="mt-2 shrink-0">
                <StateBadge state="CUSTOM" />
              </span>
              <Input
                aria-label="Engagement-specific assumption"
                value={assumption.text}
                maxLength={400}
                disabled={disabled}
                placeholder="e.g. The client provides a named executive sponsor for the duration."
                onChange={(event) => patch(assumption.id, event.target.value)}
              />
              <Button
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => remove(assumption.id)}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 border-t border-rule pt-3">
        <Button variant="secondary" size="sm" onClick={add} disabled={disabled}>
          Add engagement-specific assumption
        </Button>
        <p className="mt-2 text-[12px] text-muted">
          Custom assumptions print after the approved ones. The approved assumptions
          themselves cannot be edited or removed here.
        </p>
      </div>
    </Card>
  );
}

/** Read-only panel listing everything Sales may not change. */
export function LockedContentPanel({
  sections,
}: {
  sections: Array<{ id: string; title: string; reason: string }>;
}) {
  return (
    <Card
      title="Administrator-controlled content"
      description="Included in every proposal and not editable here."
    >
      <ul className="space-y-2.5">
        {sections.map((section) => (
          <li key={section.id} className="flex items-start gap-2.5 text-[13px]">
            <span aria-hidden="true" className="mt-0.5 shrink-0 text-muted">
              🔒
            </span>
            <span>
              <span className="block font-medium text-ink">{section.title}</span>
              <span className="block text-[12px] text-muted">{section.reason}</span>
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-3 border-t border-rule pt-3 text-[12px] text-muted">
        Changing any of these is a template or approved-content revision, requested through
        an administrator.
      </p>
    </Card>
  );
}
